import { debounce } from 'lodash';
import { useCallback, useEffect } from 'react';

type UseSelectionOptions = {
  debounceTime?: number;
};

export const useSelection = (
  getElement: () => HTMLElement | null,
  onSelectionChange: (selection: Selection | null) => void,
  options?: UseSelectionOptions,
) => {
  const { debounceTime = 200 } = options || {};

  const handleSelection = useCallback(
    debounce(() => {
      const element = getElement();

      if (!element) return onSelectionChange(null);

      const currentSelection = document.getSelection();

      if (!currentSelection || currentSelection.rangeCount === 0) return onSelectionChange(null);

      const range = currentSelection.getRangeAt(0);
      const isInsideElement = element.contains(range.commonAncestorContainer);

      onSelectionChange(isInsideElement ? currentSelection : null);
    }, debounceTime),
    [onSelectionChange, debounceTime, getElement],
  );

  // We don't attach the event listeners to the element itself
  // because most HTMLElements do not support `keyup` events
  useEffect(() => {
    document.addEventListener('mouseup', handleSelection);
    document.addEventListener('keyup', handleSelection);
    document.addEventListener('touchend', handleSelection);

    return () => {
      document.removeEventListener('mouseup', handleSelection);
      document.removeEventListener('keyup', handleSelection);
      document.removeEventListener('touchend', handleSelection);
      handleSelection.cancel();
    };
  }, [handleSelection]);
};
